import * as React from 'react';
import { useTokenValue, animate } from '@sail/engine';
import { useUpdateEffect } from '@sail/react';
import { tokens } from '@sail/tokens/deprecated';
import { tokens as $ } from '@sail/tokens';
const useTokenNumberValue = (token)=>{
    return parseInt(useTokenValue(token), 10);
};
export const translate = (placement)=>{
    const offset = $['space.150'];
    const negativeOffset = `calc(-1 * ${offset})`;
    return `translate(0, ${placement === 'bottom' ? offset : negativeOffset})`;
};
export const useAnimatedLayerIntents = ({ placement })=>{
    const easing = useTokenValue(tokens.animation.easing);
    const medium = useTokenNumberValue(tokens.animation.duration.medium);
    const short = useTokenNumberValue(tokens.animation.duration.short);
    return React.useMemo(()=>{
        return [
            animate.in({
                opacity: 0,
                transform: `scale(0.95) ${translate(placement)}`,
                easing,
                duration: medium
            }),
            animate.out({
                opacity: 0,
                transform: `scale(0.95) ${translate(placement)}`,
                easing,
                duration: short
            })
        ];
    }, [
        placement,
        easing,
        medium,
        short
    ]);
};
/**
 * Hook for managing a controlled timer that can be paused and resumed
 */ export function useControlledTimer({ duration, onComplete }) {
    const [isPaused, setIsPaused] = React.useState(false);
    const [remainingTime, setRemainingTime] = React.useState(duration);
    const timeoutRef = React.useRef(null);
    const startTimeRef = React.useRef(null);
    const clearTimer = React.useCallback(()=>{
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);
    const startTimer = React.useCallback(()=>{
        if (isPaused || remainingTime <= 0) {
            return;
        }
        startTimeRef.current = Date.now();
        timeoutRef.current = setTimeout(onComplete, remainingTime);
    }, [
        isPaused,
        remainingTime,
        onComplete
    ]);
    const pause = React.useCallback(()=>{
        if (!isPaused && timeoutRef.current && startTimeRef.current) {
            clearTimer();
            const elapsed = Date.now() - startTimeRef.current;
            setRemainingTime((prev)=>Math.max(0, prev - elapsed));
            startTimeRef.current = null;
            setIsPaused(true);
        }
    }, [
        isPaused,
        clearTimer
    ]);
    const resume = React.useCallback(()=>{
        if (isPaused) {
            setIsPaused(false);
        }
    }, [
        isPaused
    ]);
    const reset = React.useCallback(()=>{
        clearTimer();
        setRemainingTime(duration);
        setIsPaused(false);
        startTimeRef.current = null;
    }, [
        duration,
        clearTimer
    ]);
    useUpdateEffect(()=>{
        reset();
    }, [
        duration,
        reset
    ]);
    // Restart timer when resumed (only when transitioning from paused to not paused)
    const prevIsPausedRef = React.useRef(isPaused);
    React.useEffect(()=>{
        const wasResumed = prevIsPausedRef.current && !isPaused;
        prevIsPausedRef.current = isPaused;
        if (wasResumed && remainingTime > 0 && startTimeRef.current === null) {
            startTimer();
        }
    }, [
        isPaused,
        remainingTime,
        startTimer
    ]);
    // Cleanup on unmount
    React.useEffect(()=>{
        return ()=>{
            clearTimer();
        };
    }, [
        clearTimer
    ]);
    return {
        pause,
        resume,
        reset,
        start: startTimer,
        duration,
        isPaused,
        remainingTime
    };
}

//# sourceMappingURL=hooks.js.map